import { computed, Injectable, signal, type Signal, type TemplateRef } from '@angular/core';

interface RoutedChatPresentation {
  readonly owner: symbol;
  readonly standalone: Signal<boolean>;
}

export interface ChatReplyPresentation {
  readonly owner: symbol;
  readonly routedMode: Signal<boolean>;
  readonly panelOpen: Signal<boolean>;
  readonly template: TemplateRef<unknown>;
  readonly close: () => void;
}

/** Presentation owned by this layout's routed Chat; independent panel Chat never registers here. */
@Injectable()
export class LayoutCompanionPresentationService {
  private readonly routedChat = signal<RoutedChatPresentation | null>(null);
  private readonly reply = signal<ChatReplyPresentation | null>(null);

  public readonly standalone = computed(() => this.routedChat()?.standalone() ?? false);
  public readonly routedMode = computed(() => this.routedChat()?.standalone ?? null);
  public readonly replyTemplate = computed(() => {
    const routed = this.routedChat();
    const reply = this.reply();
    return routed && reply && routed.standalone === reply.routedMode && !routed.standalone() && reply.panelOpen()
      ? reply.template
      : null;
  });

  public registerReply(reply: ChatReplyPresentation): void {
    if (this.routedMode() !== reply.routedMode || this.standalone()) return;
    this.reply.set(reply);
  }

  public releaseReply(owner: symbol): void {
    if (this.reply()?.owner === owner) this.reply.set(null);
  }

  /** Existing fold semantics reclaim the slot; pending View inline keeps it until panelOpen actually changes. */
  public closeReply(): void {
    if (this.replyTemplate() !== null) this.reply()?.close();
  }

  public registerRoutedChat(owner: symbol, standalone: Signal<boolean>): void {
    this.routedChat.set({ owner, standalone });
  }

  /** A delayed teardown belongs to its own route, never to the route that replaced it. */
  public releaseRoutedChat(owner: symbol): void {
    if (this.routedChat()?.owner === owner) this.routedChat.set(null);
  }
}
